import { useQuery } from "@tanstack/react-query";
import { useEffect, useId } from "react";

import { useApi } from "../api-context";
import {
  assertDesktopInstallerMatches,
  installerApprovalCopy,
  installerApprovalSteps,
  installerLetterCopy,
  startInstallerDownload,
  type InstallerTarget,
} from "../desktop-installer";

export function DesktopInstallerDialog({
  target,
  onClose,
}: {
  readonly target: InstallerTarget | null;
  readonly onClose: () => void;
}) {
  const api = useApi();
  const titleId = useId();
  const letterId = useId();
  const stepsId = useId();
  const release = useQuery({
    queryKey: [
      "desktop-installer",
      target?.operatingSystem,
      target?.architecture,
    ],
    queryFn: async () => {
      if (target === null) {
        throw new Error("installer_target_missing");
      }
      const next = await api.desktopInstaller(
        target.operatingSystem,
        target.architecture,
      );
      assertDesktopInstallerMatches(next, target);
      return next;
    },
    enabled: target !== null,
    retry: false,
  });

  useEffect(() => {
    if (target === null) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, target]);

  if (target === null) {
    return null;
  }

  const artifact = release.data?.manifest.artifact;
  const error = release.error;
  const steps = installerApprovalSteps(target.operatingSystem);
  const letter = installerLetterCopy(target.operatingSystem);
  const ready = release.data !== undefined;

  return (
    <section
      className="decision-panel desktop-installer-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={`${letterId} ${stepsId}`}
    >
      <header>
        <div>
          <p className="utility-label">Desktop app</p>
          <h2 id={titleId}>Install {target.label}.</h2>
          <div id={letterId} className="desktop-installer-dialog__letter">
            <p>{letter.greeting}</p>
            <p>{letter.body}</p>
            <p className="desktop-installer-dialog__signoff">
              {letter.closing}
              <br />
              {letter.name}
              <br />
              {letter.title}
            </p>
          </div>
        </div>
        <button
          className="icon-action"
          type="button"
          aria-label="Close desktop installer"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      <ol id={stepsId} className="desktop-installer-dialog__steps">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      {artifact === undefined ? null : (
        <div className="desktop-installer-dialog__checksum">
          <p>
            {artifact.filename} · SHA-256 <code>{artifact.sha256}</code>
          </p>
          <p>{installerApprovalCopy(artifact.operating_system)}</p>
        </div>
      )}
      {error == null ? null : (
        <p className="form-error" role="alert">
          {error.message.replaceAll("_", " ")}
        </p>
      )}
      <div className="decision-actions">
        <button className="secondary-action" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="primary-action"
          type="button"
          disabled={!ready}
          onClick={() => {
            if (release.data === undefined) {
              return;
            }
            startInstallerDownload(release.data);
          }}
        >
          {ready ? "Download installer" : "Loading…"}
        </button>
      </div>
    </section>
  );
}
